/**
 * 그래프 데이터 한 곳 (DESIGN §8.1 · §8.1.1 · §7.2).
 *
 * **좌표는 서버가 준 x·y 를 그대로 쓴다.** 클라이언트에서 ForceAtlas2 를 다시 돌리면
 * 같은 빌드인데도 볼 때마다 배치가 달라져 "어제 본 그 무리"를 다시 찾을 수 없다
 * (§8.1 "클라이언트 시뮬레이션 금지"). 그래서 이 훅은 좌표를 만들지 않고 나르기만 한다.
 *
 * **min_weight 는 절댓값 기준이다** (§8.1.1). CORRELATED 의 weight 는 부호 있는
 * Spearman r 이므로 `weight >= x` 로 거르면 "반대로 움직인다"는 관계가 통째로 사라진다.
 * 서버 쿼리가 `abs(weight) >= $min_weight` 이고, 화면 문구도 절댓값으로 적어야 한다.
 *
 * **그래프는 standard 고정이고 상단 필터의 영향을 받지 않는다.** `graph_nodes` 에 mode
 * 축이 없고 `graph_nodes_all` 은 필터를 인자로 받지 않는다. 필터를 쿼리 키에 넣으면
 * 사용자가 필터를 만질 때마다 **같은 응답**을 다시 받으므로 여기서 고정한다 — 대신
 * "바꿔도 안 바뀐다"를 페이지가 문장으로 적는다. 안 적으면 버그로 보인다.
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { fetchEnvelope, fetchPlain, type QueryFilters } from '@/lib/api'
import { trustClass } from '@/lib/trust'
import type { Envelope, Meta } from '@/lib/types'

/**
 * 신뢰도 테두리 어휘 (§8.6 시그니처 3).
 *
 * **선 모양과 그 이름을 한 표에 둔다.** 캔버스는 대시 배열이 필요하고 범례·표는
 * 이름이 필요한데, 둘을 다른 파일에 나눠 두면 대시만 바뀌고 범례 문구가 남아
 * 거짓말이 된다. 경계값은 `lib/trust.ts` 하나에서 온다.
 */
export interface TrustMark {
  cls: ReturnType<typeof trustClass>
  /** 표·범례에 찍히는 이름 */
  mark: string
  /** canvas `setLineDash` 인자. 빈 배열이 실선 */
  dash: number[]
  label: string
}

export const TRUST_MARKS: TrustMark[] = [
  { cls: 'trust-high', mark: '실선', dash: [], label: '실선 — 커버리지 80% 이상' },
  { cls: 'trust-mid', mark: '파선', dash: [4, 3], label: '파선 — 50~80%' },
  { cls: 'trust-low', mark: '점선', dash: [1, 3], label: '점선 — 50% 미만' },
]

function markOf(coverage: number | null): TrustMark | undefined {
  if (coverage == null) return undefined
  const cls = trustClass(coverage)
  return TRUST_MARKS.find((t) => t.cls === cls)
}

/** 커버리지 → 테두리 이름. 결측은 "모름"이지 0 이 아니므로 따로 적는다 */
export function trustMark(coverage: number | null): string {
  return markOf(coverage)?.mark ?? '점선 (커버리지 없음)'
}

/** 커버리지 → 대시 패턴. 커버리지가 없으면 가장 흐린 선으로 둔다 */
export function trustDash(coverage: number | null): number[] {
  return markOf(coverage)?.dash ?? [1, 3]
}

/**
 * 엣지 타입. 두 종뿐이므로 이 목록이 곧 화면의 토글 단위다.
 *
 * 서버는 `edge_types` 를 **빈 목록 = 전부**로 해석한다. 그래서 "하나도 안 켠 상태"를
 * 그대로 보내면 정반대(전부)가 그려진다 — 토글과 화면이 어긋나는 최악의 버그다.
 * 아래 `edgeExtra` 와 클라이언트 필터가 그 구멍을 함께 막는다.
 */
export const EDGE_TYPES = [
  {
    key: 'SIMILAR_TO',
    label: '포트폴리오 유사',
    hint: '지역·타입·시설·용량 분포 벡터의 코사인 유사도',
  },
  {
    key: 'CORRELATED',
    label: '이상률 동조',
    hint: '일간 이상률을 1차 차분한 뒤의 Spearman 상관 — 부호가 있다',
  },
] as const

export type EdgeType = (typeof EDGE_TYPES)[number]['key']

export const ALL_EDGE_TYPES: EdgeType[] = EDGE_TYPES.map((t) => t.key)

/** 슬라이더 하한. 빌드가 이보다 약한 관계를 애초에 계산하지 않았다 (manifest 참조) */
export const WEIGHT_FLOOR = 0.5
export const DEFAULT_MIN_WEIGHT = 0.6

export interface GraphNode {
  node_id: string
  node_type: string
  label: string
  n_chargers: number
  /** 결측이 있다 (실측 24곳) — 0 으로 채우지 않는다 */
  availability: number | null
  coverage: number | null
  /** 레이아웃 산출물이 비면 null 이다. 좌표 없는 노드를 지우면 "배치 실패"가 "없는 사업자"가 된다 */
  x: number | null
  y: number | null
  layout_id: string | null
}

export interface GraphEdge {
  src: string
  dst: string
  edge_type: string
  /** SIMILAR_TO 는 코사인(양수), CORRELATED 는 **부호 있는** Spearman r */
  weight: number
  p_value: number | null
  /** 차분 전 상관 — 차분이 지운 허위 관계를 되짚는 증거 (§8.1) */
  r_raw: number | null
  n_days: number | null
}

interface GraphData {
  level: string
  nodes: GraphNode[]
  edges: GraphEdge[]
  /** **하드코딩 금지** — 원형 배치와 레이아웃 실패를 사용자가 구분해야 한다 (§8.1.1) */
  layout_id: string | null
}

interface NeighborsData {
  root: string
  hops: number
  nodes: GraphNode[]
  node_ids: string[]
  edges: GraphEdge[]
}

/* ── manifest.graph 진단값 (CORRELATED 0건의 사유가 여기 있다) ────────── */

export interface SimilarityDiag {
  n_operators_total: number
  n_operators_scored: number
  n_dropped_min_chargers: number
  n_pairs: number
  n_edges: number
  min_cosine: number
  min_chargers: number
  reasons: string[]
}

export interface CorrelationDiag {
  n_operators_eligible: number
  n_pairs_total: number
  /** 실측 0. "엣지가 없다"가 아니라 "검정한 쌍이 0개" 라는 뜻이다 */
  n_pairs_tested: number
  n_pairs_short_window: number
  n_edges: number
  /** 0 이지만 "허위 엣지가 없었다"가 아니다 — 검정한 쌍이 0개라 뺄 것도 없었다 */
  n_spurious_removed_by_diff: number
  n_observed_days: number
  max_common_days: number
  max_diff_pairs: number
  min_days_required: number
  min_abs_r: number
  fdr_q: number
  max_q_passed: number | null
  reasons: string[]
}

export interface LayoutDiag {
  layout_id: string
  n_nodes: number
  n_edges: number
  n_isolated: number
  iterations: number
  seed: number
  reasons: string[]
}

interface GraphManifest {
  graph?: {
    mode: string
    similarity: SimilarityDiag
    correlation: CorrelationDiag
    layout: LayoutDiag
  }
}

/**
 * 고립 노드는 지우지 않고 사유와 함께 목록으로 남긴다 (§8.1 · 절대원칙 2).
 *
 * **사유를 문장이 아니라 종류로 들고 다닌다.** 화면에서 빠진 이유가 셋인데 설명은
 * 하나뿐이면 거짓말이 된다 — `dropped` 는 좌표 자체가 군집 밖 수천 단위라 화면
 * 맞춤에서 빼야 하는 노드이고, `threshold` 는 좌표가 군집 *안*에 있는데 지금 임계에서
 * 그릴 선이 없어 빠진 노드다. 둘을 "ForceAtlas2 가 밀어냈다" 한 문장으로 묶으면
 * 임계를 올렸을 때 화면에서 사라진 대형 사업자에게 없는 사유를 붙이게 된다.
 */
export type IsolatedKind = 'no-xy' | 'dropped' | 'threshold'

export interface IsolatedNode {
  node: GraphNode
  kind: IsolatedKind
  reason: string
}

export interface GraphQuery {
  /** 드릴다운 루트. null 이면 전체 그래프(L0) */
  focus: string | null
  /** 켜진 엣지 타입 */
  types: EdgeType[]
  /** 절댓값 임계 */
  minWeight: number
}

/**
 * 그래프는 필터 축이 없다 — mode 만 계약상 필수라 standard 로 고정한다.
 * 상단 필터 바를 쿼리 키에 넣지 않는 이유는 파일 머리말에 적었다.
 */
const GRAPH_FILTERS: QueryFilters = { mode: 'standard' }

/**
 * `edge_types` 는 반복 파라미터인데 `toSearchParams` 의 extra 는 키당 값 하나다.
 * 타입이 **정확히 하나**일 때만 서버에 넘기고, 둘 다면 필터가 없는 것과 같으므로
 * 보내지 않는다. 하나도 없으면 서버가 '전부'로 해석하므로 역시 보내지 않고
 * 아래 클라이언트 필터가 0개로 만든다 — 토글과 화면이 절대 어긋나지 않게.
 */
function edgeExtra(types: EdgeType[], minWeight: number): Record<string, string | number> {
  const extra: Record<string, string | number> = { min_weight: minWeight }
  const only = types.length === 1 ? types[0] : undefined
  if (only) extra.edge_types = only
  return extra
}

export interface GraphState {
  /** 캔버스에 그릴 노드 — 현재 임계에서 엣지가 하나 이상 붙은 노드만 */
  nodes: GraphNode[]
  edges: GraphEdge[]
  /** 응답에 있던 노드 전부 (고립 포함). 표는 이것을 다 보여준다 */
  allNodes: GraphNode[]
  isolated: IsolatedNode[]
  degree: Map<string, number>
  /** 양끝 노드가 응답에 없어 그리지 못한 엣지 수 — 조용히 버리지 않는다 */
  nOrphanEdges: number
  level: string
  /** 응답이 말한 배치 id. null 이면 좌표가 없거나 섞였다는 뜻이다 */
  layoutId: string | null
  meta: Meta | undefined
  isLoading: boolean
  error: Error | null
  refetch: () => void
  similarity: SimilarityDiag | null
  correlation: CorrelationDiag | null
  layout: LayoutDiag | null
}

export function useGraph({ focus, types, minWeight }: GraphQuery): GraphState {
  const extra = edgeExtra(types, minWeight)
  const extraKey = JSON.stringify(extra)

  const l0 = useQuery({
    queryKey: ['graph', 'L0', extraKey],
    queryFn: ({ signal }) =>
      fetchEnvelope<GraphData>('/graph', GRAPH_FILTERS, { level: 'L0', ...extra }, signal),
    staleTime: 5 * 60 * 1000,
    enabled: focus == null,
  })

  // 드릴다운은 1 hop 고정이다. 2~3 hop 은 L0 와 비슷한 밀도로 돌아와 "들어간 의미"가 없다
  const local = useQuery({
    queryKey: ['graph/neighbors', focus ?? '', extraKey],
    queryFn: ({ signal }) =>
      fetchEnvelope<NeighborsData>(
        '/graph/neighbors',
        GRAPH_FILTERS,
        { node_id: focus ?? '', hops: 1, ...extra },
        signal,
      ),
    staleTime: 5 * 60 * 1000,
    enabled: focus != null,
  })

  // App 이 이미 같은 키로 받아 두므로 대개 캐시에서 온다 (재요청 아님)
  const manifest = useQuery({
    queryKey: ['meta', 'manifest'],
    queryFn: ({ signal }) => fetchPlain<GraphManifest>('/meta/manifest', signal),
    staleTime: 60 * 1000,
    retry: 1,
  })

  const active = focus == null ? l0 : local
  const env = active.data as Envelope<GraphData | NeighborsData> | undefined
  const diag = (manifest.data as GraphManifest | undefined)?.graph
  const similarity = diag?.similarity ?? null

  const shaped = useMemo(() => {
    const rawNodes = env?.data.nodes ?? []
    const rawEdges = env?.data.edges ?? []
    // 켠 타입만 남긴다. 서버 필터를 못 쓰는 경우(둘 다·하나도 안 켬)를 여기서 덮는다
    const kept = rawEdges.filter((e) => (types as string[]).includes(e.edge_type))

    const byId = new Map(rawNodes.map((n) => [n.node_id, n]))
    const degree = new Map<string, number>()
    const edges: GraphEdge[] = []
    let nOrphanEdges = 0
    for (const e of kept) {
      // 양끝이 응답에 없으면 그릴 수 없다. 개수를 세어 각주로 밝힌다
      if (!byId.has(e.src) || !byId.has(e.dst)) {
        nOrphanEdges += 1
        continue
      }
      edges.push(e)
      degree.set(e.src, (degree.get(e.src) ?? 0) + 1)
      degree.set(e.dst, (degree.get(e.dst) ?? 0) + 1)
    }

    const floor = similarity?.min_chargers ?? null
    const nodes: GraphNode[] = []
    const isolated: IsolatedNode[] = []
    for (const n of rawNodes) {
      if (n.x == null || n.y == null) {
        // 좌표가 없다고 지우면 "배치를 못 했다"가 "그 사업자가 없다"로 바뀐다
        isolated.push({
          node: n,
          kind: 'no-xy',
          reason: '좌표 없음 — 레이아웃 산출물에 이 노드가 없다',
        })
      } else if ((degree.get(n.node_id) ?? 0) === 0) {
        const dropped = floor != null && n.n_chargers < floor
        isolated.push({
          node: n,
          kind: dropped ? 'dropped' : 'threshold',
          reason: dropped
            ? `엣지 계산에서 제외 — 충전기 ${floor}기 미만 (좌표가 군집 밖)`
            : '현재 임계에서 연결 없음 — 좌표는 군집 안이다',
        })
      } else {
        nodes.push(n)
      }
    }

    return { nodes, edges, allNodes: rawNodes, isolated, degree, nOrphanEdges }
  }, [env, types, similarity])

  /**
   * `/graph/neighbors` 응답에는 layout_id 가 없어 노드에서 읽어야 한다. 이때 첫 노드
   * 하나만 보면 서로 다른 배치가 섞여 있어도 "forceatlas2" 라고 단언하게 된다 —
   * 화면은 이 값을 "이 그림을 만든 것"으로 인쇄하므로 확인한 만큼만 말한다.
   * 하나로 모이지 않으면 null 로 두고 화면이 "좌표 없음 / 혼재"라 적는다.
   */
  const layoutId = useMemo(() => {
    if (env == null) return null
    if ('layout_id' in env.data) return env.data.layout_id
    const ids = new Set(shaped.nodes.map((n) => n.layout_id))
    return ids.size === 1 ? (shaped.nodes[0]?.layout_id ?? null) : null
  }, [env, shaped])

  return {
    ...shaped,
    level: env && 'level' in env.data ? env.data.level : focus == null ? 'L0' : 'local',
    layoutId,
    meta: env?.meta,
    isLoading: active.isLoading,
    error: active.error as Error | null,
    refetch: () => void active.refetch(),
    similarity,
    correlation: diag?.correlation ?? null,
    layout: diag?.layout ?? null,
  }
}
